import { cn } from '@/lib/utils';
import { format, getISOWeek } from 'date-fns';

interface DateDisplayProps {
  /**
   * The date to display - can be Date object, ISO string, or null/undefined
   */
  value?: Date | string | null;
  /**
   * Date format string (date-fns format)
   * @default 'PPP' - e.g., "January 15, 2024"
   */
  dateFormat?: string;
  /**
   * Whether to show the week number
   * @default true
   */
  showWeekNumber?: boolean;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
  /**
   * Additional CSS classes for the week number
   */
  weekNumberClassName?: string;
}

/**
 * Consistently displays dates with optional ISO 8601 week numbers
 *
 * @example
 * // Full date with week number
 * <DateDisplay value={new Date('2024-01-15')} />
 * // Output: "January 15, 2024 (W3)"
 *
 * @example
 * // Short date format
 * <DateDisplay value="2024-06-15" dateFormat="yyyy-MM-dd" />
 * // Output: "2024-06-15 (W24)"
 *
 * @example
 * // Without week number
 * <DateDisplay value={date} showWeekNumber={false} />
 * // Output: "January 15, 2024"
 */
export function DateDisplay({
  value,
  dateFormat = 'yyyy-MM-dd',
  showWeekNumber = true,
  className,
  weekNumberClassName,
}: DateDisplayProps) {
  // Handle null/undefined
  if (!value) {
    return <span className={cn('text-muted-foreground', className)}>-</span>;
  }

  try {
    const date = typeof value === 'string' ? new Date(value) : value;
    const weekNum = showWeekNumber ? getISOWeek(date) : null;

    return (
      <span className={className}>
        {format(date, dateFormat)}
        {weekNum && (
          <span
            className={cn(
              'text-muted-foreground ml-2 text-sm',
              weekNumberClassName,
            )}
          >
            W{weekNum}
          </span>
        )}
      </span>
    );
  } catch {
    // Invalid date - display as string
    return <span className={className}>{String(value)}</span>;
  }
}
